import { Card, Col, Row } from 'antd';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { SubSubtitle } from '../../components';
import { compressionOptions } from '../../constants';

import {
  errorHandler,
  heicAndheifConvert,
  myImageCompression
} from '../../utils';
import { instanceRef } from '../../utils/axios';
import {
  CedulaAdelanteUpload,
  CedulaAtrasUpload,
  CedulaVerdeAdelanteUpload,
  CedulaVerdeAtrasUpload,
  getBase64
} from '../vehiculo/SolicitudVehiculoForm';

const DocumentosCartaVerdeUpload = ({
  screens,
  pasoDocumento,
  setDocumentosData,
  idCotizacion,
  empresaId,
  aseguradoraId,
  token,
  cedulaVerde,
  cedulaIdentidad,
  setLoadingPrincipalImagen
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loadingCVA, setLoadingCVA] = useState(false);
  const [loadingCVD, setLoadingCVD] = useState(false);
  const [, setLoadingBefore] = useState(false);
  const [loadingCIA, setLoadingCIA] = useState(false);
  const [loadingCID, setLoadingCID] = useState(false);
  const {
    cedulaVerde1,
    setCedulaVerde1,
    cedulaVerde2,
    setCedulaVerde2
  } = cedulaVerde;
  const {
    cedulaIdentidad1,
    setCedulaIdentidad1,
    cedulaIdentidad2,
    setCedulaIdentidad2
  } = cedulaIdentidad;

  const formatosImagen = 'image/jpeg,image/heif,image/heic, image/png';

  const colStyles = {
    display: 'table',
    textAlign: 'center',
    justifyContent: 'center'
  };

  const maxWidth = 1096;

  const uploadFile = async (file, type, setter, setLoading) => {
    try {
      const validTypes = ['CVA', 'CVD', 'CIA', 'CID'];
      if (!validTypes.includes(type)) return;

      setLoading(true);

      // Obtener reCaptchaToken
      const reCaptchaToken = await executeRecaptcha('file/upload');

      // Convertir y comprimir la imagen
      const convertedFile = await heicAndheifConvert(file.file);
      const compressedFile = await myImageCompression(
        convertedFile,
        compressionOptions
      );

      // Crear FormData
      const formData = createFormData(compressedFile, reCaptchaToken);

      // Determinar la URL
      const url = determineUploadUrl(type);

      // Subir el archivo
      const response = await instanceRef.instance.post(url, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      const documentData = response?.data?.data;
      setDocumentosData((prevData) => ({
        ...prevData,
        [type]: { ...documentData } // Agregamos o actualizamos el valor para la clave dinámicas
      }));

      if (documentData) {
        // Validar el documento
        const isDocumentValid = await validateDocument(type, documentData);
        if (!isDocumentValid) {
          setLoading(false);
          return;
        }

        // Procesar imagen en Base64 y actualizar el estado
        getBase64(compressedFile, (imageUrl) => {
          setter({
            imagen: imageUrl,
            extension: compressedFile.type.split('/')[1],
            nombre: `${type}.${compressedFile.type.split('/')[1]}`
          });
          setLoading(false);
        });
      }
    } catch (error) {
      errorHandler(error, error.message);
      setLoading(false);
    }
  };

  // Función auxiliar para crear FormData
  const createFormData = (file, reCaptchaToken) => {
    const formData = new FormData();
    formData.append('cedula', file);
    formData.append('reCaptchaToken', reCaptchaToken);
    formData.append('token', token || 'default-token');
    formData.append('empresaId', empresaId); // Valor por defecto
    formData.append('cotizacionId', idCotizacion);
    formData.append('aseguradoraId', aseguradoraId);
    return formData;
  };

  // Función auxiliar para determinar la URL de subida
  const determineUploadUrl = (type) => {
    const baseUrl = 'file/upload';
    if (type === 'CIA' || type === 'CID') {
      return `${baseUrl}?tipo=${type}`;
    }
    return `${baseUrl}CedulaVerde?tipo=${type}&idcotizacion=${idCotizacion}`;
  };

  // Validación de documentos según tipo
  const validateDocument = async (type, data) => {
    if (type === 'CIA' || type === 'CID') {
      const validationErrors = await validateCedulaIdentidad(data, type);
      if (!isEmpty(validationErrors)) {
        errorHandler(null, validationErrors);
        return false;
      }
    }

    if (type === 'CVA' || type === 'CVD') {
      const validationErrors = validateCedulaVerde(data, type);
      if (!isEmpty(validationErrors)) {
        errorHandler(null, validationErrors);
        return false;
      }
    }

    return true;
  };

  const validateCedulaIdentidad = (fileData, cara) => {
    let retorno = null;
    return new Promise((resolve) => {
      switch (cara) {
        case 'CIA':
          if (!fileData.nombres || !fileData.cedula) {
            retorno =
              'Favor subir otra imagen. Te recomendamos evitar el uso de flash o imágenes con brillo excesivo.';
          }
          break;
        default:
          break;
      }
      resolve(retorno);
    });
  };

  const validateCedulaVerde = (fileData, cara) => {
    let retorno = null;
    switch (cara) {
      case 'CVD': // Cedula Verde Detras
        if (isEmpty(fileData.marca?.descripcion)) {
          retorno = 'Ingrese el lado correcto de la cédula verde';
        } else if (!fileData.marca?.descripcion) {
          retorno =
            'Favor subir otra imagen. Te recomendamos evitar el uso de flash o imágenes con brillo excesivo';
        }
        break;
      case 'CVA': // Cedula Verde Adelante
        if (!fileData.esCedulaVerdeValida) {
          retorno =
            'Favor subir otra imagen. Te recomendamos evitar el uso de flash o imágenes con brillo excesivo';
        }
        break;
      default:
        break;
    }
    return retorno;
  };

  useEffect(() => {
    setLoadingPrincipalImagen(
      loadingCIA || loadingCID || loadingCVA || loadingCVD
    );
  }, [
    loadingCIA,
    loadingCID,
    loadingCVA,
    loadingCVD,
    setLoadingPrincipalImagen
  ]);

  const renderUploadCV = () => (
    <>
      <Col
        xs={screens?.md ? undefined : 24}
        md={screens?.md ? undefined : 24}
        style={colStyles}
      >
        <CedulaVerdeAdelanteUpload
          formatosImagen={formatosImagen}
          cargado={cedulaVerde1}
          setCargado={setCedulaVerde1}
          setLoading={setLoadingCVA}
          setLoadingBefore={setLoadingBefore}
          loading={loadingCVA}
          uploadFile={uploadFile}
          idCotizacion={11062}
        />
      </Col>
      <Col
        xs={screens?.md ? undefined : 24}
        md={screens?.md ? undefined : 24}
        style={colStyles}
      >
        <CedulaVerdeAtrasUpload
          formatosImagen={formatosImagen}
          cargado={cedulaVerde2}
          setCargado={setCedulaVerde2}
          setLoading={setLoadingCVD}
          setLoadingBefore={setLoadingBefore}
          loading={loadingCVD}
          uploadFile={uploadFile}
          idCotizacion={11062}
        />
      </Col>
    </>
  );

  const renderUploadCI = () => (
    <>
      <Col
        xs={screens?.md ? undefined : 24}
        md={screens?.md ? undefined : 24}
        style={colStyles}
      >
        <CedulaAdelanteUpload
          formatosImagen={formatosImagen}
          cargado={cedulaIdentidad1}
          setCargado={setCedulaIdentidad1}
          setLoading={setLoadingCIA}
          setLoadingBefore={setLoadingBefore}
          loading={loadingCIA}
          uploadFile={uploadFile}
        />
      </Col>
      <Col
        xs={screens?.md ? undefined : 24}
        md={screens?.md ? undefined : 24}
        style={colStyles}
      >
        <CedulaAtrasUpload
          formatosImagen={formatosImagen}
          cargado={cedulaIdentidad2}
          setCargado={setCedulaIdentidad2}
          setLoading={setLoadingCID}
          setLoadingBefore={setLoadingBefore}
          loading={loadingCID}
          uploadFile={uploadFile}
        />
      </Col>
    </>
  );

  return (
    <Card size="small">
      <Row
        gutter={[16, 16]}
        justify="center"
        style={{
          maxWidth,
          textAlign: 'center'
        }}
      >
        <Col xs={24} md={24} style={colStyles}>
          <SubSubtitle>{`${
            pasoDocumento === 0
              ? 'Cédula Verde (Obligatorio)'
              : 'Cédula de Identidad (Obligatorio)'
          }`}</SubSubtitle>
        </Col>
        {pasoDocumento === 0 ? renderUploadCV() : renderUploadCI()}
      </Row>
    </Card>
  );
};

export default DocumentosCartaVerdeUpload;
